import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Upload as UploadIcon, FileText, Download, Save, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const DEFAULT_BUDGET_ROWS = [
  { label: "Human Resources", unit: "hrs", amount: 0 },
  { label: "Google Ads spend", unit: "$", amount: 0 },
  { label: "YouTube Ads spend", unit: "$", amount: 0 },
  { label: "Facebook boost budget", unit: "$", amount: 0 },
  { label: "Instagram boost budget", unit: "$", amount: 0 },
  { label: "LinkedIn boost budget", unit: "$", amount: 0 },
];

const UNIT_OPTIONS = ["$", "AUD", "USD", "hrs", "posts", "campaigns", "videos", "%"];

export default function ClientBriefPage() {
  const { clientId } = useParams();
  const [summary, setSummary] = useState("");
  const [budget, setBudget] = useState([]);
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rollup, setRollup] = useState(null);
  const [defaultRate, setDefaultRate] = useState(0);

  const load = useCallback(async () => {
    const [{ data }, settings] = await Promise.all([
      api.get(`/clients/${clientId}/brief`),
      api.get(`/app-settings`).then((r) => r.data).catch(() => ({ default_hourly_rate: 0 })),
    ]);
    const fallback = Number(settings?.default_hourly_rate) || 0;
    setDefaultRate(fallback);
    setSummary(data.brief_summary || "");
    setBudget(
      data.budget_items?.length
        ? data.budget_items.map((r) => ({
            ...r,
            hourly_rate:
              r.unit === "hrs" && !Number(r.hourly_rate) ? fallback : r.hourly_rate,
          }))
        : DEFAULT_BUDGET_ROWS.map((r) => ({
            ...r,
            hourly_rate: r.unit === "hrs" ? fallback : 0,
          }))
    );
    setDocs(data.documents || []);
    try {
      const { data: r } = await api.get(`/clients/${clientId}/budget-rollup`);
      setRollup(r);
    } catch (err) {
      // Rollup is optional UI — keep the page usable, but surface in dev
      if (process.env.NODE_ENV !== "production") {
        logger.debug("budget-rollup fetch failed:", err?.message);
      }
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const saveSummary = async () => {
    setBusy(true);
    try {
      await api.patch(`/clients/${clientId}/brief`, { brief_summary: summary });
      toast.success("Brief summary saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const saveBudget = async () => {
    setBusy(true);
    try {
      const cleaned = budget
        .filter((r) => r.label && r.label.trim())
        .map((r) => ({
          item_id: r.item_id,
          label: r.label.trim(),
          unit: r.unit || "$",
          amount: Number(r.amount) || 0,
          hourly_rate: Number(r.hourly_rate) || 0,
          notes: (r.notes || "").trim(),
        }));
      await api.patch(`/clients/${clientId}/brief`, { budget_items: cleaned });
      toast.success("Budget saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const addRow = () => setBudget([
    ...budget,
    { item_id: `new_${Date.now()}_${budget.length}`, label: "", unit: "$", amount: 0, hourly_rate: defaultRate, notes: "" },
  ]);
  const updateRow = (i, key, val) =>
    setBudget(budget.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const removeRow = (i) => setBudget(budget.filter((_, idx) => idx !== i));

  // Compute monthly $ cost per row: hrs × rate when unit === 'hrs', else amount when unit is $/AUD/USD
  const _rowMonthlyCost = (r) => {
    const amt = Number(r.amount) || 0;
    const rate = Number(r.hourly_rate) || 0;
    if ((r.unit || "").toLowerCase() === "hrs") return amt * rate;
    if (["$", "AUD", "USD"].includes(r.unit || "$")) return amt;
    return 0;
  };
  const totalMonthly = budget.reduce((sum, r) => sum + _rowMonthlyCost(r), 0);
  const _fmt$ = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const uploadDoc = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${clientId}/brief-documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setFile(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const removeDoc = async (id) => {
    try {
      await api.delete(`/brief-documents/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Failed"); }
  };

  const download = (d) => {
    fetch(`${API}/brief-documents/${d.document_id}/download`, {
      credentials: "include",
    }).then((r) => r.blob()).then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = d.filename;
      link.click();
      URL.revokeObjectURL(link.href);
    }).catch(() => toast.error("Download failed"));
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="brief-page">
      {/* LEFT — Strategy summary */}
      <Card className="rounded-sm border-zinc-200 lg:col-span-2">
        <CardContent className="p-6 space-y-3">
          <div>
            <div className="label-overline">Strategy summary</div>
            <h2 className="font-display font-bold text-xl tracking-tight">What we are trying to achieve</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Free-text overview of the agreed strategy, KPIs, and any context the team needs to keep in mind.
            </p>
          </div>
          <Textarea rows={20} value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="e.g. Position GBE Group as the Hunter Region's #1 commercial FM specialist. Drive +25% organic traffic to gbelectrical.com.au, generate 30 qualified leads/month across the 6 sites…"
            className="rounded-sm" data-testid="brief-summary-input" />
          <Button onClick={saveSummary} disabled={busy}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="brief-save-button">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Save summary
          </Button>
        </CardContent>
      </Card>

      {/* Branding (palette + fonts) moved to the new "Settings" tab. */}

      {/* Brief files (signed-off documents) — moved above the budget section per UX request */}
      <Card className="rounded-sm border-zinc-200 lg:col-span-2">
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="label-overline">Signed-off documents</div>
            <h2 className="font-display font-bold text-xl tracking-tight">Campaign Briefs &amp; Time Frames</h2>
            <p className="text-sm text-zinc-500 mt-1">PDFs / Word / images — whatever the client signed off on.</p>
          </div>

          <div className="flex items-center gap-2">
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rounded-sm flex-1" data-testid="brief-file-input" />
            <Button onClick={uploadDoc} disabled={busy || !file}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="brief-upload-button">
              <UploadIcon className="h-3.5 w-3.5 mr-1.5" /> Upload
            </Button>
          </div>

          <div className="space-y-2 max-h-[36vh] overflow-y-auto">
            {docs.map((d) => (
              <div key={d.document_id}
                className="flex items-center gap-3 p-3 border border-zinc-200 rounded-sm bg-white"
                data-testid={`brief-doc-${d.document_id}`}>
                <FileText className="h-4 w-4 text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{d.filename}</div>
                  <div className="text-xs text-zinc-500">{(d.size_bytes / 1024).toFixed(1)} KB</div>
                </div>
                <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => download(d)}
                  data-testid={`brief-download-${d.document_id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                  onClick={() => removeDoc(d.document_id)}
                  data-testid={`brief-delete-${d.document_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            {docs.length === 0 && (
              <div className="text-center text-sm text-zinc-500 py-4">No documents uploaded yet.</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* MIDDLE — Budget */}
      <Card className="rounded-sm border-zinc-200 lg:col-span-2">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-overline">Current budget</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Total Resources Budget</h2>
              <p className="text-sm text-zinc-500 mt-1">Hours, ad spend, boost budgets — anything that bounds what the team can do.</p>
            </div>
            <DollarSign className="h-5 w-5 text-zinc-400" />
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_70px_100px_100px_110px_auto] gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-1 pb-1 border-b border-zinc-100">
              <div>Label</div>
              <div>Unit</div>
              <div className="text-right pr-2">Amount</div>
              <div className="text-right pr-2">Rate $/hr</div>
              <div className="text-right pr-2">Monthly $</div>
              <div></div>
            </div>
            {budget.map((row, i) => {
              const isHrs = (row.unit || "").toLowerCase() === "hrs";
              const monthly = _rowMonthlyCost(row);
              const rowKey = row.item_id || `idx-${i}`;
              return (
                <div key={rowKey} className="grid grid-cols-[1fr_70px_100px_100px_110px_auto] gap-2 items-center"
                  data-testid={`budget-row-${i}`}>
                  <Input value={row.label} onChange={(e) => updateRow(i, "label", e.target.value)}
                    placeholder="e.g. Google Ads spend"
                    className="rounded-sm h-9 text-sm" data-testid={`budget-label-${i}`} />
                  <Select value={row.unit} onValueChange={(v) => updateRow(i, "unit", v)}>
                    <SelectTrigger className="rounded-sm h-9 text-sm" data-testid={`budget-unit-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="0.01" value={row.amount}
                    onChange={(e) => updateRow(i, "amount", e.target.value)}
                    className="rounded-sm h-9 text-sm text-right font-mono"
                    data-testid={`budget-amount-${i}`} />
                  <Input type="number" step="0.01" value={row.hourly_rate || ""}
                    onChange={(e) => updateRow(i, "hourly_rate", e.target.value)}
                    placeholder={isHrs ? "0.00" : "—"}
                    disabled={!isHrs}
                    className="rounded-sm h-9 text-sm text-right font-mono disabled:bg-zinc-50 disabled:text-zinc-400"
                    data-testid={`budget-rate-${i}`} />
                  <div className="text-right text-sm font-mono text-zinc-700 px-2"
                    data-testid={`budget-monthly-${i}`}>
                    {monthly > 0 ? _fmt$(monthly) : <span className="text-zinc-300">—</span>}
                  </div>
                  <Button size="sm" variant="ghost" className="rounded-sm text-red-600 h-9 w-9 p-0"
                    onClick={() => removeRow(i)} data-testid={`budget-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addRow}
              className="rounded-sm w-full" data-testid="budget-add-row">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add budget line
            </Button>

            {/* Total monthly marketing cost */}
            <div className="grid grid-cols-[1fr_70px_100px_100px_110px_auto] gap-2 items-center pt-3 mt-2 border-t-2 border-zinc-900">
              <div className="font-display font-bold text-sm tracking-tight col-span-5">
                Total monthly marketing cost
              </div>
              <div className="text-right text-base font-mono font-bold text-[#002FA7] px-2"
                data-testid="budget-monthly-total">
                {_fmt$(totalMonthly)}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 -mt-1">
              HR rows: monthly $ = hours × rate. Money rows ($, AUD, USD): monthly $ = amount.
              Other units (posts, campaigns…) are not summed.
            </p>
          </div>

          <div className="pt-2 border-t border-zinc-100">
            <Button onClick={saveBudget} disabled={busy}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="budget-save-button">
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save budget
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* RIGHT — Brief files card relocated above the budget section */}

      {/* COMBINED ROLLUP — full width */}
      {rollup && (rollup.rollup?.length > 0 || rollup.business_units?.length > 0) && (
        <Card className="rounded-sm border-zinc-200 lg:col-span-2" data-testid="budget-rollup">
          <CardContent className="p-6 space-y-3">
            <div>
              <div className="label-overline">Combined</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Total Resources Allocated Across All Properties</h2>
              <p className="text-sm text-zinc-500 mt-1">
                Sums Client-wide spend and every property&apos;s sub-budget. Useful for monthly invoicing and total-spend reporting.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
                    <th className="py-2">Line</th>
                    <th className="py-2">Unit</th>
                    <th className="py-2 text-right">Combined total</th>
                    <th className="py-2">Scope breakdown</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.rollup.map((r, i) => (
                    <tr key={`${r.label}-${r.unit}`} className="border-b border-zinc-100"
                      data-testid={`rollup-row-${i}`}>
                      <td className="py-2 font-semibold">{r.label}</td>
                      <td className="py-2">{r.unit}</td>
                      <td className="py-2 text-right font-mono font-semibold">{Number(r.total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td className="py-2 text-xs text-zinc-500">
                        {r.by_scope.filter((b) => b.amount > 0).map((b) => (
                          <span key={b.scope} className="inline-block mr-3">
                            {b.scope}: <strong>{Number(b.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                  {rollup.rollup.length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-xs text-zinc-500">No budget set yet on client or properties.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

